/**
 * 학습 행동 로그를 모아 두었다가 서버로 보내는 부수효과 전용 컴포넌트다. 화면에는 아무것도 그리지 않는다.
 *
 * `useAuditLogger`(객관식 챕터)와 `useEssayAuditLogger`(서술형 챕터)가 이벤트가 생길 때마다
 * Recoil 큐(`auditLogQueueState`)에 로그를 쌓아 두면, 이 컴포넌트가 큐와 네트워크 상태를
 * 지켜보다가 온라인일 때 순서대로 서버에 올린다. 큐는 localStorage 에도 저장되므로
 * (recoil-persist 적용) 네트워크가 끊기거나 페이지를 새로고침해도 로그가 사라지지 않는다.
 * `pages/_app.tsx` 에서 앱 전체에 딱 한 번 마운트된다.
 *
 * 주의: 큐에는 여러 유저의 로그가 섞여 쌓일 수 있다(기기 공유·재로그인 등). 그래서 전송 전에
 * 현재 로그인한 유저 ID로 한 번 걸러, 다른 사람 로그를 잘못 보내는 일을 막는다.
 */
import { useEffect, useRef } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { auditLogQueueState, networkStatusState, userState } from "@/recoil/atom";
import { postAuditLog } from "pages/api/audit/postAuditLog";

/**
 * 큐에 쌓인 로그를 네트워크 상태와 현재 유저를 확인해 순서대로 서버에 보낸다.
 * 렌더링할 화면 요소가 없어 항상 null 을 반환한다.
 */
export const AuditLogSync = () => {
  const [queue, setQueue] = useRecoilState(auditLogQueueState);
  const networkStatus = useRecoilValue(networkStatusState);
  const user = useRecoilValue(userState);
  const isProcessing = useRef(false);

  useEffect(() => {
    const processQueue = async () => {
      // 이미 처리 중이거나 큐가 비었으면 중단
      if (isProcessing.current || queue.length === 0) return;
      
      // 온라인일 때만 전송
      if (networkStatus !== "normal" && networkStatus !== "recovered") return;

      // 현재 유저 ID 확인
      const currentUserId = user?.id || Number(sessionStorage.getItem("userId")) || 0;
      if (!currentUserId) return;

      isProcessing.current = true;

      try {
        // 네트워크 복구 직후라면 연결이 안정될 때까지 아주 잠시 대기
        if (networkStatus === "recovered") {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }

        // 현재 유저의 로그만 필터링해서 처리
        const currentBatch = queue.filter(log => log.userId === currentUserId);
        if (currentBatch.length === 0) {
          isProcessing.current = false;
          return;
        }

        let successIds: number[] = []; // 인덱스 대신 유니크한 식별자가 있으면 좋겠지만, 일단 순서대로 처리
        let successCount = 0;

        console.log(`[AuditLogSync] 유저(${currentUserId})의 로그 ${currentBatch.length}개 전송 시작...`);

        // 로그 순서를 지키려고 한 건씩 순차 전송한다. 중간에 하나가 실패하면
        // 뒤 로그들을 건너뛰고 먼저 보내는 대신 즉시 멈춘다. 이렇게 하면 다음 실행에서
        // 실패한 지점부터 다시 이어서 보낼 수 있다(순서가 섞이는 것을 막기 위한 선택).
        for (const log of currentBatch) {
          try {
            await postAuditLog(log);
            successCount++;
          } catch (error) {
            console.error("[AuditLogSync] 전송 실패, 중단:", error);
            break;
          }
        }

        // 성공한 개수만큼 큐에서 제거 (필터링된 유저의 로그 중 앞부분부터 successCount만큼)
        if (successCount > 0) {
          setQueue((prev) => {
            const next = [...prev];
            // 해당 유저의 로그를 찾아 앞에서부터 successCount개 삭제
            let removed = 0;
            return next.filter(log => {
              if (log.userId === currentUserId && removed < successCount) {
                removed++;
                return false;
              }
              return true;
            });
          });
        }
      } finally {
        isProcessing.current = false;
      }
    };

    processQueue();
  }, [networkStatus, queue, user?.id, setQueue]);

  return null;
};
